import React from 'react';
import PropTypes from 'prop-types';
import qs from 'qs';
import formatMoney from 'utils/formatters/Money';
import { uniq, assign, cloneDeep } from 'lodash';
import queryObject from 'utils/query-object';
import AccountingActionCreators from 'actions/AccountingActionCreators';
import CustomerSearch from 'components/site/customer-search';
import InvoiceSearch from 'components/site/invoice-search';
import DropdownMenu from 'components/v2/dropdowns/DropdownMenu';
import DropdownItem from 'components/v2/dropdowns/DropdownItem';
import { Menu } from '@headlessui/react';
import SessionStore from 'stores/SessionStore';
import Container from 'components/v2/container';
import Card from 'components/v2/card';
import Badge from 'components/v2/Badge';
import moment from 'moment';
import DateRangeFilter from 'components/time/DateRangeFilter';
import Multiselect from 'components/v2/multi-select';
import CalendarIcon from 'components/v2/icons/CalendarIcon';
import { AuthorizationWrapper } from 'components/wrappers/AuthorizationWrapper';
import { REGIONS } from 'constants/Regions';
import AccountingWebAPIUtils from 'utils/AccountingWebAPIUtils';
import { Trans, withTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import classnames from 'classnames';
import { showToast } from 'stores/ToastStore';
import { getDefaultDateRangeFilter } from 'helpers/date-range';
import Alert from 'components/ui/Alert';
import InvoiceList from './invoice-list';
import MultipleInvoiceModal from './multiple-invoice-modal';

const shipmentTypeStatuses = [
  { name: 'SHORTHAUL', slug: 'shorthaul', localizationSlug: 'SHORTHAUL' },
  {
    name: 'DRAYAGE',
    slug: 'drayage',
    localizationSlug: 'DRAYAGE',
    titleize: true,
  },
];

const zeroStats = {
  count: 0,
  total: 0,
  paid: 0,
  credit_memos: 0,
  credit_memo_total: 0,
};

const DEFAULT_DATE_RANGE_VALUES = {
  startValue: 1,
  startTimeUnit: 'month',
  startKey: {
    key: 'date_created',
  },
};

class InvoiceModule extends React.Component {
  static getInvoices(filters) {
    if (SessionStore.isAdmin()) {
      return AccountingActionCreators.getAdminInvoices(filters);
    }
    return AccountingActionCreators.getShipperInvoices(
      SessionStore.getUser()._id,
      filters,
    );
  }

  static getInvoicesStats(filters) {
    if (SessionStore.isAdmin()) {
      return AccountingWebAPIUtils.getAdminInvoicesStats(filters);
    }
    return AccountingWebAPIUtils.getShipperInvoicesStats(
      SessionStore.getUser()._id,
      filters,
    );
  }

  constructor(props) {
    super(props);
    const initial = {
      filters: {
        paginate: true,
        page: 1,
        page_size: 20,
        date_range: getDefaultDateRangeFilter(DEFAULT_DATE_RANGE_VALUES),
        shipmentTypes: {
          shorthaul: true,
          drayage: true,
        },
      },
      invoices: [],
      count: 0,
      selectedInvoices: [],
      showSendEmailModal: false,
      invoiceQueries: [],
      shipperQueries: [],
      dateCreatedQuery: { start: null, end: null },
      invoice_type: 'debit',
      selectedCreditMemos: [],
      invoiceStats: zeroStats,
      creditMemoStats: zeroStats,
      managedStats: zeroStats,
      pageLoading: false,
    };
    initial.filters = { ...initial.filters, ...this.getUrlParams() };
    initial.invoiceQueries = initial.filters.number
      ? initial.filters.number
      : [];

    this.state = initial;
  }

  componentDidMount() {
    this.queryData(this.state.filters);
    this.updateShipmentTypeList();
  }

  isCreditMemoAvailableInSelectedInvoices = () => {
    let selectedCreditMemos;
    if (this.state.selectedInvoices.length) {
      // getting all the selectedinvoices
      const selectedInvoices = this.state.selectedInvoices.map((invoiceId) =>
        this.state.invoices.find((invoice) => invoice._id === invoiceId),
      );
      // Filtering credit memo ids
      const invoicesWithCreditMemos = selectedInvoices.filter(
        (invoice) => !!(invoice.credit_memos && invoice.credit_memos.length),
      );
      if (invoicesWithCreditMemos.length) {
        selectedCreditMemos = invoicesWithCreditMemos.map(
          (invoice) => invoice.credit_memos[0]._id,
        );
      }
      // checking credit memo is present in selected invoices
      const isCreditMemoAvailable = selectedInvoices.some(
        (invoice) => !!(invoice.credit_memos && invoice.credit_memos.length),
      );

      this.setState({ isCreditMemoAvailable, selectedCreditMemos });
    }
  };

  updateSelectedInvoices = (selectedInvoices) => {
    this.setState({ selectedInvoices }, () => {
      this.isCreditMemoAvailableInSelectedInvoices();
    });
  };

  updateShipmentTypeList() {
    shipmentTypeStatuses.forEach((_type) => {
      if (this.state.filters.shipmentTypes[_type.slug] === true) {
        _type.selected = true;
      }
    });
  }

  sendEmail() {
    let invoices = [];
    switch (this.state.invoice_type) {
      case 'credit': {
        invoices = [...this.state.selectedCreditMemos];
        break;
      }
      case 'both': {
        invoices = [
          ...this.state.selectedInvoices,
          ...this.state.selectedCreditMemos,
        ];
        break;
      }
      default: {
        invoices = [...this.state.selectedInvoices];
      }
    }
    return (emails) => {
      invoices.forEach((invoiceId) => {
        AccountingActionCreators.emailInvoice(invoiceId, emails);
      });
    };
  }

  onPaginationSelect(page) {
    const { filters } = this.state;
    filters.page = page;
    this.setState({ filters } , () => {
      this.queryData(filters);
    });
  }

  getUrlParams() {
    const rawParams = queryObject(this.props.location);
    const params = {};
    const booleanFilters = ['shipmentTypes'];
    const arrayFilters = ['shipper', '_id'];
    Object.keys(rawParams).forEach((param) => {
      const key = decodeURIComponent(param);
      const val = decodeURIComponent(rawParams[param]);
      if (!key) {
        return;
      }

      if (!/\[/.test(key)) {
        params[key] = val;
      } else {
        const keyElements = key.split('[');
        const topKey = keyElements[0];
        const subKey = keyElements[1].replace(']', '');
        if (arrayFilters.includes(topKey)) {
          if (!params[topKey]) {
            params[topKey] = [val];
          } else {
            params[topKey] = [...params[topKey], val];
          }
        } else {
          if (!params[topKey]) {
            params[topKey] = {};
          }
          // unfortunately qs doesn't convert strings to booleans, so we'll have to do that ourselves
          if (booleanFilters.indexOf(topKey) > -1) {
            params[topKey][subKey] = val === 'true';
          } else {
            params[topKey][subKey] = val;
          }
        }
      }
    });

    return params;
  }

  queryData(filters) {
    const { t } = this.props;
    this.setState({ pageLoading: true });
    if (SessionStore.isAdmin()) {
      InvoiceModule.getInvoicesStats(filters)
        .then((results) => {
          const res = results.entity;
          const invoiceStats = res.find((r) => r._id === 'debit') || zeroStats;
          const creditMemoStats =
            res.find((r) => r._id === 'credit') || zeroStats;
          const managedStats =
            res.find((r) => r._id === 'managed') || zeroStats;
          const { count } = invoiceStats;
          if (count < (filters.page - 1) * filters.page_size) {
            filters.page = 1;
          }
          this.setState(
            (prevState) => {
              const totalPages = Math.ceil(count / prevState.filters.page_size);
              const update = {
                count,
                filters,
                totalPages,
                invoiceStats,
                creditMemoStats,
                managedStats,
              };
              const newState = assign(cloneDeep(prevState), update);
              return newState;
            },
            () => {
              this.updateUrl(filters);
            },
          );
        })
        .catch((e) => {
          return showToast(
            t('INVOICE_DATA_ERROR'),
            t('ERROR_FETCHING_INVOICE_STATS'),
            'error',
          );
        });
    }

    InvoiceModule.getInvoices(filters)
      .then((results) => {
        const { invoices, totalCount } = results.entity;
        this.setState(
          (prevState) => {
            const newState = cloneDeep(prevState);
            const invoiceIds = invoices.map((inv) => inv._id);
            newState.filters._id = (newState.filters._id || []).filter(
              (filteredId) => invoiceIds.includes(filteredId),
            );
            newState.invoices = invoices;
            newState.count = totalCount;
            newState.totalPages = parseInt(totalCount / prevState.filters.page_size, 10);
            return newState;
          },
          () => {
            this.updateUrl(filters);
            this.setState({ pageLoading: false });
          },
        );
      })
      .catch((e) => {
        this.setState({ pageLoading: false });
        return showToast(
          t('INVOICE_DATA_ERROR'),
          t('ERROR_FETCHING_INVOICE_DATA'),
          'error',
        );
      });
  }

  updateUrl(filters) {
    this.props.history.push(
      `${this.props.location.pathname}?${qs.stringify(filters)}`,
    );
  }

  extendFilters(filter) {
    return Object.assign({}, this.state.filters, filter);
  }

  filterByShipper(shipper) {
    this.setState((prevState) => {
      const shipperQueries = uniq(
        (prevState.shipperQueries || []).concat(shipper),
      );
      const filters = uniq(
        (prevState.filters && prevState.filters.shipper
          ? prevState.filters.shipper
          : []
        ).concat(shipper.value),
      );
      this.queryData(this.extendFilters({ shipper: filters }));
      return { shipperQueries };
    });
  }

  filterByInvoice(invoices = []) {
    if (!invoices.length) {
      return;
    }

    this.setState((prevState) => {
      const invoiceIds = this.getDocumentsIds(invoices);
      const invoiceQueries = uniq(
        (prevState.invoiceQueries || []).concat(invoices),
      );
      const filters = uniq(
        (prevState.filters && prevState.filters._id
          ? prevState.filters._id
          : []
        ).concat(invoiceIds),
      );
      this.queryData(this.extendFilters({ _id: filters }));
      return {
        invoiceQueries,
        filters: {
          ...prevState.filters,
          _id: filters,
        },
      };
    });
  }

  handleRemoveShipper = (item) => () => {
    this.setState(
      (prevState) => {
        const shipperIndex = prevState.shipperQueries.findIndex(
          (s) => s.value === item.value,
        );
        const filterIndex = prevState.filters.shipper.findIndex(
          (f) => f === item.value,
        );
        const shipperQueries = [
          ...prevState.shipperQueries.slice(0, shipperIndex),
          ...prevState.shipperQueries.slice(shipperIndex + 1),
        ];
        const shipperFilter = [
          ...prevState.filters.shipper.slice(0, filterIndex),
          ...prevState.filters.shipper.slice(filterIndex + 1),
        ];
        let filters = prevState.filters;
        filters.shipper = shipperFilter;
        return {
          shipperQueries,
          filters,
        };
      },
      () => this.queryData(this.state.filters),
    );
  };

  getId = (document) => {
    return document?._id || document;
  };

  getDocumentsIds = (invoices = []) => {
    if (!invoices?.length) return [];
    let invoiceIds = []
    invoices.forEach(invoice => {
      const ids = [invoice._id].concat(
        ((invoice?.credit_memos || []).map((cm) => this.getId(cm)) || []).concat(
          this.getId(invoice?.original_invoice) || [],
        ),
      );
      invoiceIds = invoiceIds.concat(ids);
    });
    return invoiceIds;
  };

  getDocumentIds = (invoice) => {
    if (!invoice) return [];
    return [invoice?._id].concat(
      ((invoice?.credit_memos || []).map((cm) => this.getId(cm)) || []).concat(
        this.getId(invoice.original_invoice) || [],
      ),
    );
  };

  handleRemoveInvoice = (selectedInvoice) => () => {
    this.setState(
      (prevState) => {
        const invoiceQueries = cloneDeep(prevState.invoiceQueries);
        const invoiceIndex = invoiceQueries.findIndex(
          (invoice) => invoice.number === selectedInvoice.number,
        );

        const updatedInvoiceQueries = [
          ...invoiceQueries.slice(0, invoiceIndex),
          ...invoiceQueries.slice(invoiceIndex + 1),
        ];

        const filteredIds = uniq(
          updatedInvoiceQueries.reduce((ids, invoice) => {
            return ids.concat(this.getDocumentIds(invoice));
          }, []),
        );

        let filters = prevState.filters;
        filters._id = filteredIds;
        return {
          invoiceQueries: updatedInvoiceQueries,
          filters,
        };
      },
      () => this.queryData(this.state.filters),
    );
  };

  onShipmentTypeChanged(shipmentTypes) {
    const shipmentTypesQueries = {};
    shipmentTypes.forEach((_type) => {
      shipmentTypesQueries[_type.slug] = true;
    });
    this.queryData(this.extendFilters({ shipmentTypes: shipmentTypesQueries }));
  }

  renderShipmentTypeFilter = () => {
    const { t } = this.props;
    return (
      <div className="mb-4">
        <label className="form-label block mb-2">
          {t('FILTER_BY_SHIPMENT_TYPE')}
        </label>
        <Multiselect
          label={titleize(t('TYPE'))}
          items={shipmentTypeStatuses}
          onSelect={(shipmentTypes) =>
            this.onShipmentTypeChanged(shipmentTypes)
          }
          showSearchInput={false}
        />
      </div>
    );
  };

  renderShipperFilter = () => {
    const { t } = this.props;
    return (
      <div className="mb-4">
        <label className="form-label block mb-2">
          {t('FILTER_BY_SHIPPER')}
        </label>
        <CustomerSearch
          clearOnSelect
          type="shippers"
          onSelect={(_item, value) => this.filterByShipper(value)}
          placeholder={t('SEARCH_FOR_SHIPPER_PLACEHOLDER')}
        />
        {this.state.shipperQueries.length > 0 && (
          <div className="flex flex-wrap -mx-2 mt-2 mb-4">
            {this.state.shipperQueries.map((shipper) => (
              <div className="px-2 mb-2" key={shipper.value}>
                <Badge
                  color="green"
                  withRemove
                  onRemove={this.handleRemoveShipper(shipper)}
                >
                  {shipper.name}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  renderInvoiceFilter = () => {
    const { t } = this.props;
    return (
      <div className="mb-4">
        <label className="form-label block mb-2">
          {t('FILTER_BY_INVOICE')}
        </label>
        <InvoiceSearch
          onSelect={(invoices) => this.filterByInvoice(invoices)}
        />
        {this.state.invoiceQueries.length > 0 && (
          <div className="flex flex-wrap -mx-2 mt-2 mb-4">
            {this.state.invoiceQueries.map((invoice) => (
              <div className="px-2 mb-2" key={invoice}>
                <Badge
                  color="green"
                  withRemove
                  onRemove={this.handleRemoveInvoice(invoice)}
                >
                  {invoice.number}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  handleDateFilterChange = ({ filterType, start, end }) => {
    const dateStart = moment(start).startOf('day').toISOString();
    const dateEnd = moment(end).endOf('day').toISOString();
    this.setState((prevState) => {
      const filters = {
        key: 'date_created',
        start: dateStart,
        end: dateEnd,
      };
      this.queryData(this.extendFilters({ date_range: filters }));

      return {
        ...prevState,
        filters: {
          ...prevState.filters,
          date_range: filters,
        },
      };
    });
  };

  renderDateCreatedFilter = () => {
    const { date_range } = this.state.filters;
    const { t } = this.props;

    const defaultDateRangeFilter = getDefaultDateRangeFilter({
      options: 'object',
      ...DEFAULT_DATE_RANGE_VALUES,
    });

    return (
      <div className="mb-4">
        <label className="form-label block mb-2">
          {t('FILTER_BY_DATE_CREATED')}
        </label>
        <DateRangeFilter
          buttonClassName="flex w-full button bg-white border shadow justify-center"
          onChange={this.handleDateFilterChange}
          initialStart={defaultDateRangeFilter.start}
          initialEnd={defaultDateRangeFilter.end}
          startKey={defaultDateRangeFilter.key}
          label={
            <div className="flex space-x-1">
              <span className="text-green">
                <CalendarIcon />
              </span>
              <span className="font-normal text-sm text-grey-darker">
                {t('DATE_RANGE')}
              </span>
            </div>
          }
          updateBool={date_range?.start && date_range?.end}
          isClearEnabled={false}
        />
      </div>
    );
  };

  renderHeader() {
    const isAdmin = SessionStore.isAdmin();
    const isShipper = SessionStore.isShipper();
    const { t } = this.props;

    if (!isAdmin && !isShipper) {
      return null;
    }

    return (
      <div className="mb-6">
        {isShipper && <Card>{this.renderFilters()}</Card>}
        {!isShipper && (
          <Card
            grayFooter
            footer={() => (
              <div className="flex flex-wrap justify-between items-center">
                {this.state.invoiceStats && (
                  <p className="text-base mb-4 sm:my-0">
                    <Trans
                      i18nKey="TOTAL_INVOICE_TOTALLING"
                      values={{
                        total_invoice_count: this.state.invoiceStats.count,
                        total_invoice_amount: formatMoney(
                          this.state.invoiceStats.total,
                          DEFAULT_CURRENCY,
                        ),
                      }}
                      components={[<strong />, <strong />]}
                    />
                  </p>
                )}
                {this.state.creditMemoStats && (
                  <p className="text-base mb-4 sm:my-0">
                    <Trans
                      i18nKey="TOTAL_CREDIT_MEMO_TOTALLING"
                      values={{
                        total_credit_memo_count:
                          this.state.creditMemoStats.count,
                        total_credit_memo_amount: formatMoney(
                          this.state.creditMemoStats.total,
                          DEFAULT_CURRENCY,
                        ),
                      }}
                      components={[<strong />, <strong />]}
                    />
                  </p>
                )}
                <p className="text-base mb-4 sm:my-0">
                  <Trans
                    i18nKey="INVOICE_BALANCE"
                    values={{
                      invoice_balance: formatMoney(
                        this.state.invoiceStats.total -
                          this.state.invoiceStats.paid,
                        DEFAULT_CURRENCY,
                      ),
                    }}
                    components={[<strong />]}
                  />
                </p>
                <AuthorizationWrapper excludedRegions={[REGIONS.AP]}>
                  <div className="w-48">
                    <DropdownMenu title={t('ACTIONS')}>
                      <Menu.Item
                        id="email-invoices"
                        disabled={!this.state.selectedInvoices.length}
                        key="invoices"
                      >
                        <DropdownItem
                          onClick={() => {
                            this.setState({
                              showSendEmailModal: true,
                              invoice_type: 'debit',
                            });
                          }}
                        >
                          <span
                            className={classnames({
                              'text-gray-400':
                                !this.state.selectedInvoices.length,
                            })}
                          >
                            {t('EMAIL_INVOICES')}
                          </span>
                        </DropdownItem>
                      </Menu.Item>
                      <Menu.Item
                        id="email-credit-memos"
                        disabled={!this.state.isCreditMemoAvailable}
                        key="credit-memos"
                      >
                        <DropdownItem
                          onClick={() => {
                            this.setState({
                              showSendEmailModal: true,
                              invoice_type: 'credit',
                            });
                          }}
                        >
                          <span
                            className={classnames({
                              'text-gray-400':
                                !this.state.isCreditMemoAvailable,
                            })}
                          >
                            {t('EMAIL_CREDIT_MEMOS')}
                          </span>
                        </DropdownItem>
                      </Menu.Item>
                      <Menu.Item
                        id="email-both"
                        disabled={!this.state.isCreditMemoAvailable}
                        key="both"
                      >
                        <DropdownItem
                          onClick={() => {
                            this.setState({
                              showSendEmailModal: true,
                              invoice_type: 'both',
                            });
                          }}
                        >
                          <span
                            className={classnames({
                              'text-gray-400':
                                !this.state.isCreditMemoAvailable,
                            })}
                          >
                            {t('EMAIL_BOTH')}
                          </span>
                        </DropdownItem>
                      </Menu.Item>
                    </DropdownMenu>
                  </div>
                </AuthorizationWrapper>
              </div>
            )}
          >
            {this.renderFilters()}
          </Card>
        )}
      </div>
    );
  }

  renderFilters = () => {
    const isShipper = SessionStore.isShipper();

    return (
      <div className="flex flex-wrap -mx-4">
        {!isShipper && (
          <div className="sm:w-1/2 lg:w-1/4 w-full px-4">
            {this.renderShipperFilter()}
          </div>
        )}

        <div className="sm:w-1/2 lg:w-1/4 w-full px-4">
          {this.renderInvoiceFilter()}
        </div>

        {!isShipper && (
          <div className="sm:w-1/2 lg:w-1/6 w-full px-4">
            {this.renderShipmentTypeFilter()}
          </div>
        )}

        <div className="sm:w-1/2 lg:w-1/6 w-full px-4">
          {this.renderDateCreatedFilter()}
        </div>
      </div>
    );
  };

  render() {
    const isShipper = SessionStore.isShipper();
    const isAPACRegion = REGION_CODE === REGIONS.AP;
    const { t } = this.props;
    if (isShipper && isAPACRegion) {
      return (
        <Container className="py-8">
          <Alert title={t('REQUIRE_PERMISSION_TO_VIEW_PAGE')} type="error" />
        </Container>
      );
    }
    return (
      <Container className="py-8">
        {this.renderHeader()}
        <div className="mt-6">
          <InvoiceList
            total={this.state.count}
            perPage={parseInt(this.state.filters.page_size, 10)}
            page={parseInt(this.state.filters.page, 10)}
            pages={this.state.totalPages}
            onPaginationSelect={(page) => this.onPaginationSelect(page)}
            invoices={this.state.invoices}
            sort={this.state.filters.sort}
            updateSelectedInvoices={this.updateSelectedInvoices}
            loading={this.state.pageLoading}
          />
        </div>
        {this.state.showSendEmailModal && (
          <MultipleInvoiceModal
            show={this.state.showSendEmailModal}
            onSend={this.sendEmail()}
            onHide={() => this.setState({ showSendEmailModal: false })}
            invoices={this.state.selectedInvoices}
          />
        )}
      </Container>
    );
  }
}

InvoiceModule.propTypes = {
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
};

export default withTranslation()(InvoiceModule);
